import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, FileText, ChevronRight, Building2, Pencil, Trash2, Upload, User } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import { requisitionService, type RequisitionSortColumn } from '@core/supabase/services/requisitionService';
import { clientService } from '@core/supabase/services/clientService';
import type { Client, Requisition, RequisitionStatus } from '@core/types';
import { REQUISITION_STATUSES } from '@core/enums';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { SyncStatusBadge } from '@shared/components/SyncStatusBadge';
import { useToast } from '@shared/hooks/useToast';
import { ListConfigureButton } from '@shared/components/ui/ListConfigureButton';
import { ListOperationsDropdown } from '@shared/components/ui/ListOperationsDropdown';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';
import { ConfigureListColumnsModal, colClass } from '@shared/components/ui/ConfigureListColumnsModal';
import { useListColumnVisibility } from '@shared/hooks/useListColumnVisibility';
import type { ListColumnCatalogItem } from '@shared/lib/listColumnPreferences';
import { listExportNotImplemented } from '@shared/lib/listExportStub';
import { downloadRequisitionsCsv } from './requisitionsListCsv';
import { formatCreatedWhen } from '@shared/lib/formatCreatedWhen';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { TatChip } from '@shared/components/ui/TatChip';
import { EmptyState } from '@shared/components/ui/EmptyState';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { TablePagination } from '@shared/components/ui/TablePagination';
import { DataTableToolbar } from '@shared/components/ui/DataTableToolbar';
import { DataTableDeleteConfirmModal } from '@shared/components/ui/DataTableDeleteConfirmModal';
import { SortableTh, EnrichColumnTh } from '@shared/components/ui/SortableTableHeader';
import { TableAvatar } from '@shared/components/ui/TableAvatar';
import { RowHoverActions } from '@shared/components/ui/RowHoverActions';
import { GenericManageRightsModal } from '@shared/components/list/GenericManageRightsModal';
import { RequisitionNewWizardModal } from './RequisitionNewWizardModal';
import { RequisitionIntakeWizardPage } from './RequisitionIntakeWizardPage';

/** Light + dark: readable contrast on white / charcoal rows */
const STATUS_BADGE: Record<RequisitionStatus, string> = {
  draft: 'bg-slate-200 text-slate-900 border-slate-300 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700',
  awaiting_client:
    'bg-sky-100 text-sky-950 border-sky-300 dark:bg-sky-900/40 dark:text-sky-200 dark:border-sky-800',
  submitted: 'bg-amber-100 text-amber-950 border-amber-300 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-800',
  approved: 'bg-sky-100 text-sky-950 border-sky-300 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-800',
  rejected: 'bg-rose-100 text-rose-950 border-rose-300 dark:bg-rose-900/35 dark:text-rose-200 dark:border-rose-800',
  sourcing: 'bg-emerald-100 text-emerald-950 border-emerald-300 dark:bg-green-900/40 dark:text-green-200 dark:border-green-800',
  filled: 'bg-teal-100 text-teal-950 border-teal-300 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-800',
  on_hold: 'bg-yellow-100 text-yellow-950 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-200 dark:border-yellow-800',
  cancelled: 'bg-red-100 text-red-900 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-900',
};

function formatShortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const s = formatDateTimeGmt(iso);
  return s === '—' ? null : s;
}

/** Who / what the row is waiting on, plus a “since” date when useful. */
function pendingFromCell(req: Requisition): { primary: string; since: string | null } {
  switch (req.status) {
    case 'draft':
      if (req.intake_wizard_step != null) {
        return { primary: 'Intake wizard', since: formatShortDate(req.updated_at) };
      }
      return { primary: '—', since: null };
    case 'awaiting_client':
      return {
        primary: 'Hiring client',
        since: formatShortDate(req.client_outreach_sent_at ?? req.updated_at),
      };
    case 'submitted': {
      const name = req.approver_user?.display_name?.trim();
      return {
        primary: name ? `Approver · ${name}` : 'Internal approval',
        since: formatShortDate(req.submitted_at),
      };
    }
    case 'approved':
      return { primary: '—', since: formatShortDate(req.approved_at) };
    case 'rejected':
      return { primary: '—', since: formatShortDate(req.rejected_at) };
    case 'sourcing':
      return { primary: '—', since: formatShortDate(req.approved_at ?? req.updated_at) };
    case 'filled':
      return { primary: '—', since: formatShortDate(req.updated_at) };
    case 'on_hold':
      return { primary: 'On hold', since: formatShortDate(req.updated_at) };
    case 'cancelled':
      return { primary: '—', since: formatShortDate(req.updated_at) };
    default:
      return { primary: '—', since: null };
  }
}

const REQUISITIONS_LIST_COLUMNS: ListColumnCatalogItem[] = [
  { id: 'role', label: 'Title' },
  { id: 'priority', label: 'Priority', optional: true },
  { id: 'intake', label: 'Intake', optional: true },
  { id: 'client', label: 'Client' },
  { id: 'dept', label: 'Department', optional: true },
  { id: 'status', label: 'Status' },
  { id: 'target_close', label: 'Target close' },
  { id: 'pending', label: 'Pending from' },
  { id: 'pending_since', label: 'Pending since', optional: true },
  { id: 'hc', label: 'Headcount', optional: true },
  { id: 'linked_job', label: 'Linked job' },
  { id: 'created', label: 'Created' },
];

function isRequisitionsTableMissing(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const o = err as { code?: string; message?: string };
  return (
    o.code === 'PGRST205' ||
    (typeof o.message === 'string' && o.message.includes('ggj_b2b_requisitions'))
  );
}

export function RequisitionsPage() {
  const { user } = useAuth();
  const perms = usePermissions();
  const { toast, showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState<Requisition[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [clients, setClients] = useState<Client[]>([]);
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<RequisitionStatus | ''>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<'missing_table' | 'other' | null>(null);
  const [listErrorMessage, setListErrorMessage] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckRef = useRef<HTMLInputElement>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [intakeModal, setIntakeModal] = useState<{ reqId: string; seed: Requisition } | null>(null);
  const [configureOpen, setConfigureOpen] = useState(false);
  const [manageRightsOpen, setManageRightsOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [sortBy, setSortBy] = useState<RequisitionSortColumn>('created_at');
  const [sortAsc, setSortAsc] = useState(false);

  const reqCatalog = useMemo(() => REQUISITIONS_LIST_COLUMNS, []);
  const { show, setColumn, reset, visible } = useListColumnVisibility(
    'requisitions',
    user?.orgId ?? null,
    reqCatalog,
  );

  useEffect(() => {
    if (!user?.orgId) return;
    clientService.list(user.orgId).then((r) => setClients(Array.isArray(r) ? r : (r as { data: Client[] }).data));
  }, [user?.orgId]);

  useEffect(() => {
    const cid = searchParams.get('clientId');
    if (cid) {
      setClientFilter(cid);
      setPage(1);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!user?.orgId) return;
    setLoading(true);
    requisitionService
      .list(user.orgId, {
        search: search || undefined,
        clientId: clientFilter || undefined,
        status: statusFilter || undefined,
        page,
        pageSize,
        sortBy,
        sortAsc,
      })
      .then((res) => {
        setListError(null);
        setListErrorMessage(null);
        const r = res as { data: Requisition[]; total: number };
        setRows(r.data);
        setTotalItems(r.total);
      })
      .catch((err: unknown) => {
        console.error('Requisitions list failed:', err);
        setRows([]);
        setTotalItems(0);
        const msg =
          err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string'
            ? (err as { message: string }).message
            : 'Could not load requisitions';
        setListErrorMessage(msg);
        setListError(isRequisitionsTableMissing(err) ? 'missing_table' : 'other');
      })
      .finally(() => setLoading(false));
  }, [user?.orgId, search, clientFilter, statusFilter, page, pageSize, sortBy, sortAsc]);

  const handleReqSort = (col: RequisitionSortColumn) => {
    if (sortBy === col) {
      setSortAsc((a) => !a);
    } else {
      setSortBy(col);
      // created_at: newest first (desc). target_close_date + title + status: asc.
      setSortAsc(col !== 'created_at');
    }
    setPage(1);
  };

  useEffect(() => {
    if (headerCheckRef.current) {
      headerCheckRef.current.indeterminate = rows.length > 0 && selectedIds.size > 0 && selectedIds.size < rows.length;
    }
  }, [rows.length, selectedIds.size]);

  const handleDeleteRequisitions = async (ids: string[]) => {
    if (!user?.orgId) return;
    if (!perms.canManageClients) {
      showToast({ title: 'You do not have permission to delete requisitions.', kind: 'error' });
      return;
    }
    if (ids.length === 0) return;
    try {
      for (const id of ids) {
        await requisitionService.delete(id);
      }
      setSelectedIds(new Set());
      const res = await requisitionService.list(user.orgId, {
        search: search || undefined,
        clientId: clientFilter || undefined,
        status: statusFilter || undefined,
        page,
        pageSize,
        sortBy,
        sortAsc,
      });
      const r = res as { data: Requisition[]; total: number };
      setRows(r.data);
      setTotalItems(r.total);
      setListError(null);
      setListErrorMessage(null);
    } catch (e) {
      showToast({ title: e instanceof Error ? e.message : 'Delete failed.', kind: 'error' });
    }
  };

  const runReqExport = useCallback(
    async (scope: 'selection' | 'all') => {
      if (!user?.orgId) return;
      try {
        const res = await requisitionService.list(user.orgId, {
          search: search || undefined,
          clientId: clientFilter || undefined,
          status: statusFilter || undefined,
          page: 1,
          pageSize: 10000,
          sortBy,
          sortAsc,
        });
        const r = res as { data: Requisition[]; total: number };
        let exportRows = r.data;
        if (scope === 'selection') {
          if (selectedIds.size === 0) {
            showToast({ title: 'Select rows to export, or use CSV — all under Operations.', kind: 'error' });
            return;
          }
          exportRows = exportRows.filter((x) => selectedIds.has(x.id));
        }
        if (exportRows.length === 0) {
          showToast({ title: 'Nothing to export for the current filters.', kind: 'error' });
          return;
        }
        downloadRequisitionsCsv(exportRows);
      } catch (e) {
        showToast({ title: e instanceof Error ? e.message : 'Export failed.', kind: 'error' });
      }
    },
    [user?.orgId, search, clientFilter, statusFilter, sortBy, sortAsc, selectedIds],
  );

  if (!user?.orgId) return null;

  return (
    <div>
      {toast}
      <SyncStatusBadge className="mb-3" />
      <PageHeader
        variant="minimal"
        breadcrumb={[{ label: 'Pipeline', href: '/requisitions' }, { label: 'Requisitions' }]}
        breadcrumbRowEnd={
          <ListOperationsDropdown
            objectHistoryHref={objectHistoryUrl('requisitions')}
            onExport={(fmt, scope) => {
              if (fmt === 'csv') void runReqExport(scope);
              else listExportNotImplemented('Requisitions', fmt, scope);
            }}
            extraActions={[
              {
                id: 'import',
                label: 'Import',
                icon: <Upload className="vscode-text-secondary" strokeWidth={2} />,
                onClick: () =>
                  showToast({ title: 'Bulk requisition import is not available. Create requisitions with New requisition.', kind: 'error' }),
              },
              {
                id: 'rights',
                label: 'Manage Rights',
                icon: <User className="vscode-text-secondary" strokeWidth={2} />,
                onClick: () => setManageRightsOpen(true),
              },
            ]}
          />
        }
      />

      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input className="input pl-9" placeholder="Search by title…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select className="input w-auto" value={clientFilter} onChange={(e) => { setClientFilter(e.target.value); setPage(1); }}>
          <option value="">All clients</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select className="input w-auto" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as RequisitionStatus | ''); setPage(1); }}>
          <option value="">All statuses</option>
          {REQUISITION_STATUSES.map((s) => (
            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      {listError === 'missing_table' && (
        <div className="mb-4 rounded-xl border border-amber-700/60 bg-amber-950/30 px-4 py-3 text-sm text-amber-100">
          <p className="font-medium text-amber-50">Requisitions database objects are missing</p>
          <p className="mt-2 text-amber-100/90">
            PostgREST cannot see <code className="rounded bg-black/30 px-1 py-0.5 text-xs">public.ggj_b2b_requisitions</code>.
            Apply migration{' '}
            <code className="rounded bg-black/30 px-1 py-0.5 text-xs">supabase/migrations/013_ggj_b2b_requisitions.sql</code>{' '}
            in the Supabase SQL Editor for this project (or run the &quot;Migration 013&quot; block in{' '}
            <code className="rounded bg-black/30 px-1 py-0.5 text-xs">run_b2b_migrations_manually.sql</code>
            ), then reload. Dashboard → Settings → API → reload schema if the table exists but the API still errors.
          </p>
        </div>
      )}

      {loading ? (
        <FullPageSpinner />
      ) : listError === 'missing_table' ? (
        <EmptyState
          icon={FileText}
          title="Requisitions not available"
          description="Run migration 013 on your Supabase project, then refresh this page."
        />
      ) : listError === 'other' ? (
        <EmptyState
          icon={FileText}
          title="Couldn’t load requisitions"
          description={listErrorMessage ?? 'Check the console and your Supabase RLS policies.'}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No requisitions yet"
          description="Capture client hiring intent before you publish a full JD. Link a live job when the role is approved."
          action={
            <button type="button" className="btn-primary" onClick={() => setWizardOpen(true)}>
              New Requisition
            </button>
          }
        />
      ) : (
        <div className="card overflow-hidden p-0">
          <DataTableToolbar
            hasTable={true}
            newLabel="New requisition"
            newOnClick={() => setWizardOpen(true)}
            toolbarEnd={
              <ListConfigureButton onClick={() => setConfigureOpen(true)} title="Table layout for requisitions" />
            }
          />
          <DataTableDeleteConfirmModal
            open={bulkDeleteOpen}
            onClose={() => setBulkDeleteOpen(false)}
            entityLabel="requisitions"
            count={selectedIds.size}
            bodyExtra="Deleting a requisition removes this hiring request. Linked jobs may be unlinked (SET NULL) rather than deleted; check your schema."
            onConfirm={() =>
              void handleDeleteRequisitions(Array.from(selectedIds)).then(() => setBulkDeleteOpen(false))
            }
            requireTypedConfirm="DELETE"
          />
          {selectedIds.size > 0 && (
            <div className="ggj-data-bulk-bar flex flex-wrap items-center gap-2 border-b border-[color-mix(in_srgb,var(--vscode-border-primary)_50%,transparent)] px-4 py-2">
              <span className="text-sm font-medium vscode-text-primary">{selectedIds.size} selected</span>
              <button
                type="button"
                className="btn-secondary inline-flex items-center gap-1 text-xs"
                disabled={selectedIds.size !== 1}
                onClick={() => {
                  const id = Array.from(selectedIds)[0];
                  if (id) navigate(`/requisitions/${id}`);
                }}
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
              <button
                type="button"
                className="btn-secondary inline-flex items-center gap-1 text-xs"
                disabled={!perms.canManageClients}
                onClick={() => setBulkDeleteOpen(true)}
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          )}
          <div className="overflow-x-auto min-w-0">
          <table className="ggj-data-table min-w-[900px]">
            <thead>
              <tr className="text-left">
                <th className="w-10">
                  <input
                    ref={headerCheckRef}
                    type="checkbox"
                    checked={rows.length > 0 && selectedIds.size === rows.length}
                    onChange={() => {
                      if (selectedIds.size === rows.length) setSelectedIds(new Set());
                      else setSelectedIds(new Set(rows.map((x) => x.id)));
                    }}
                    className="rounded border-gray-600"
                  />
                </th>
                <SortableTh
                  label="Title"
                  column="title"
                  current={sortBy}
                  asc={sortAsc}
                  onSort={handleReqSort}
                  className={colClass(show('role'), '')}
                  cellPaddingClass="px-2 py-2"
                />
                <th className={colClass(show('priority'), 'px-2 py-2')}>Priority</th>
                <th className={colClass(show('intake'), 'px-2 py-2')}>Intake</th>
                <th className={colClass(show('client'), 'px-2 py-2')}>Client</th>
                <th className={colClass(show('dept'), 'px-2 py-2')}>Dept.</th>
                <SortableTh
                  label="Status"
                  column="status"
                  current={sortBy}
                  asc={sortAsc}
                  onSort={handleReqSort}
                  className={colClass(show('status'), '')}
                  cellPaddingClass="px-2 py-2"
                />
                <SortableTh
                  label="Target close"
                  column="target_close_date"
                  current={sortBy}
                  asc={sortAsc}
                  onSort={handleReqSort}
                  className={colClass(show('target_close'), 'whitespace-nowrap')}
                  cellPaddingClass="px-2 py-2"
                />
                <th className={colClass(show('pending'), 'px-2 py-2 min-w-[9rem]')}>Pending from</th>
                <th className={colClass(show('pending_since'), 'px-2 py-2 whitespace-nowrap')}>Pending since</th>
                <th className={colClass(show('hc'), 'px-2 py-2')}>HC</th>
                <th className={colClass(show('linked_job'), 'px-2 py-2')}>Linked Job</th>
                <SortableTh
                  label="Created"
                  column="created_at"
                  current={sortBy}
                  asc={sortAsc}
                  onSort={handleReqSort}
                  className={colClass(show('created'), 'whitespace-nowrap')}
                  cellPaddingClass="px-2 py-2"
                />
                <EnrichColumnTh />
              </tr>
            </thead>
            <tbody>
              {rows.map((req) => {
                const pending = pendingFromCell(req);
                return (
                  <tr key={req.id} className={selectedIds.has(req.id) ? 'bg-brand-500/10' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(req.id)}
                        onChange={() => {
                          const n = new Set(selectedIds);
                          if (n.has(req.id)) n.delete(req.id);
                          else n.add(req.id);
                          setSelectedIds(n);
                        }}
                        className="rounded border-gray-600"
                      />
                    </td>
                    <td className={colClass(show('role'), 'px-2 py-2')}>
                      <div className="flex items-center gap-2">
                        <TableAvatar name={req.title} size={20} />
                        <Link to={`/requisitions/${req.id}`} className="text-[13px] font-medium vscode-text-primary hover:text-brand-600 dark:hover:text-brand-400 truncate">
                          {req.title}
                        </Link>
                      </div>
                    </td>
                    <td className={colClass(show('priority'), 'px-2 py-2 text-sm vscode-text-secondary')}>
                      {req.priority ? `${req.priority.charAt(0).toUpperCase() + req.priority.slice(1)} priority` : '—'}
                    </td>
                    <td className={colClass(show('intake'), 'px-2 py-2')}>
                      {req.status === 'draft' && req.intake_wizard_step != null ? (
                        <button
                          type="button"
                          onClick={() => setIntakeModal({ reqId: req.id, seed: req })}
                          className="text-xs font-medium text-brand-600 hover:text-brand-500 dark:text-brand-400 dark:hover:text-brand-300 text-left"
                        >
                          Step {req.intake_wizard_step}/5
                        </button>
                      ) : (
                        <span className="vscode-text-tertiary">—</span>
                      )}
                    </td>
                    <td className={colClass(show('client'), 'px-2 py-2')}>
                      <span className="inline-flex items-center gap-1.5 vscode-text-primary">
                        <Building2 className="w-4 h-4 shrink-0 vscode-text-tertiary" />
                        {req.client?.name ?? '—'}
                      </span>
                    </td>
                    <td className={colClass(show('dept'), 'vscode-text-primary text-sm px-2 py-2')}>
                      {req.department?.trim() ? req.department : <span className="vscode-text-tertiary">—</span>}
                    </td>
                    <td className={colClass(show('status'), 'px-2 py-2')}>
                      <span className={`inline-flex rounded-md border px-2.5 py-1 text-sm font-medium capitalize ${STATUS_BADGE[req.status]}`}>
                        {req.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className={colClass(show('target_close'), 'px-2 py-2')}>
                      <TatChip
                        targetCloseDate={req.target_close_date ?? null}
                        closedAt={req.closed_at ?? null}
                        compact
                      />
                      {!req.target_close_date && !req.closed_at && (
                        <span className="vscode-text-tertiary text-sm">—</span>
                      )}
                    </td>
                    <td className={colClass(show('pending'), 'text-sm px-2 py-2')}>
                      <span className="vscode-text-primary font-medium leading-snug">{pending.primary}</span>
                    </td>
                    <td className={colClass(show('pending_since'), 'text-sm px-2 py-2 tabular-nums vscode-text-secondary whitespace-nowrap')}>
                      {pending.since ? pending.since : '—'}
                    </td>
                    <td className={colClass(show('hc'), 'vscode-text-primary font-medium tabular-nums px-2 py-2')}>{req.headcount}</td>
                    <td className={colClass(show('linked_job'), 'px-2 py-2')}>
                      {req.job_id ? (
                        <Link to={`/jobs/${req.job_id}`} className="text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300">
                          {req.job?.title ?? 'View job'}
                        </Link>
                      ) : (
                        <span className="vscode-text-tertiary">—</span>
                      )}
                    </td>
                    <td className={colClass(show('created'), 'text-sm vscode-text-secondary tabular-nums whitespace-nowrap px-2 py-2')}>
                      {formatCreatedWhen(req.created_at)}
                    </td>
                    <RowHoverActions />
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          <TablePagination
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            entityLabel="requisitions"
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </div>
      )}

      {user?.orgId && (
        <RequisitionNewWizardModal
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          onCreated={(req) => navigate(`/requisitions/${req.id}`)}
          orgId={user.orgId}
          userId={user.id}
        />
      )}

      {intakeModal && (
        <RequisitionIntakeWizardPage
          onModalClose={() => setIntakeModal(null)}
          modalReqId={intakeModal.reqId}
          modalSeed={intakeModal.seed}
        />
      )}
      <ConfigureListColumnsModal
        open={configureOpen}
        onClose={() => setConfigureOpen(false)}
        entityLabel="Requisitions"
        columns={REQUISITIONS_LIST_COLUMNS}
        value={visible}
        onChange={setColumn}
        onReset={reset}
      />
      <GenericManageRightsModal
        open={manageRightsOpen}
        onClose={() => setManageRightsOpen(false)}
        orgId={user?.orgId ?? null}
        entityKey="requisitions"
        subtitle="Per-user access for the requisitions list — stored locally per browser."
      />
    </div>
  );
}
